// Apolo IDE — usePermissions hook (S4 · E4 live wiring).
//
// Loads `.apollo/permissions.json` once per session root via the Electron file
// IPC (window.apolo.file.read). Mirrors useSteering: cache per root, ref kept in
// sync for stable send callbacks, safe fallback on web/tests (no Electron).
//
// LAW (retrocompatible): the file is OPT-IN. Absent → { enabled:false } (auto-review
// off, behavior unchanged). Present (even malformed) → { enabled:true } with a
// deny-by-default policy (parsePermissions falls back safely).

import { useEffect, useRef, useState } from 'react';
import { parsePermissions, PERMISSIONS_REL_PATH, PERMISSIONS_LEGACY_REL_PATH } from '../lib/permissions.js';

const OFF = { enabled: false, policy: null };

/**
 * @param {string|undefined|null} root - session root (must start with '/')
 * @returns {{ enabled: boolean, policy: object|null, ref: React.MutableRefObject<{enabled:boolean, policy:object|null}> }}
 */
export function usePermissions(root) {
  const [state, setState] = useState(OFF);
  const ref = useRef(OFF);
  const mountedRef = useRef(true);
  const loadedRootRef = useRef(null);

  useEffect(() => { ref.current = state; }, [state]);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    if (!root || typeof root !== 'string' || !root.startsWith('/')) {
      setState(OFF); ref.current = OFF; loadedRootRef.current = null;
      return undefined;
    }
    if (loadedRootRef.current === root) return undefined;

    const readFn = window.apolo?.file?.read;
    if (typeof readFn !== 'function') {
      setState(OFF); ref.current = OFF;
      return undefined;
    }
    loadedRootRef.current = root;

    let cancelled = false;
    (async () => {
      let content = null;
      try {
        const res = await readFn({ root, filePath: PERMISSIONS_REL_PATH });
        content = typeof res?.content === 'string' ? res.content : null;
      } catch {
        content = null; // ENOENT / IPC failure → treat as absent
      }
      // E9-T02 — fallback legado .apollo/ (double-L): projeto antigo não quebra.
      if (content == null) {
        try {
          const res = await readFn({ root, filePath: PERMISSIONS_LEGACY_REL_PATH });
          content = typeof res?.content === 'string' ? res.content : null;
        } catch { content = null; }
      }
      if (cancelled || !mountedRef.current) return;
      if (content == null) {
        // No policy file → auto-review OFF (retrocompatible).
        setState(OFF); ref.current = OFF;
        return;
      }
      // Present (even malformed) → ON with a deny-by-default policy.
      const next = { enabled: true, policy: parsePermissions(content).value };
      setState(next); ref.current = next;
    })();

    return () => { cancelled = true; };
  }, [root]);

  return { enabled: state.enabled, policy: state.policy, ref };
}

export default usePermissions;
